/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */

import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';

import { useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { jsx } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu';

import messages from './messages';
import { RefineDropdownItems } from './RefineDropdownItems';

export interface RefineDropdownBaseItem {
	children?: RefineDropdownBaseItem[];
	icon?: ReactNode;
	onClick?: () => void;
	title: MessageDescriptor;
}

export interface RefineDropdownClickableItem extends RefineDropdownBaseItem {
	children?: undefined;
	icon: ReactNode;
	onClick: () => void;
	title: MessageDescriptor;
}

export interface RefineDropdownSubMenuItem extends RefineDropdownBaseItem {
	children: Omit<RefineDropdownClickableItem, 'icon'>[];
	icon: ReactNode;
	onClick?: undefined;
	title: MessageDescriptor;
}

export type RefineDropdownItem = RefineDropdownClickableItem | RefineDropdownSubMenuItem;

interface Props {
	items: RefineDropdownItem[];
	onClick?: () => void;
}

export function RefineDropdown({ items, onClick }: Props) {
	const { formatMessage } = useIntl();

	const [isOpened, setIsOpened] = useState(false);
	const [currentOpenedSubMenu, setCurrentOpenedSubMenu] =
		useState<RefineDropdownSubMenuItem | null>(null);

	const handleOpenChange = useCallback(
		({ isOpen }: { isOpen: boolean }) => {
			isOpen && onClick && onClick();
			setIsOpened(isOpen);
			if (!isOpen) {
				setCurrentOpenedSubMenu(null);
			}
		},
		[onClick],
	);

	const handleItemClick = useCallback(() => {
		setIsOpened(false);
		setCurrentOpenedSubMenu(null);
	}, []);

	const handleSubMenuOpenChanged = useCallback(
		(subMenu: RefineDropdownSubMenuItem, isOpen: boolean) => {
			setCurrentOpenedSubMenu((current) => {
				// Close the current opened sub menu if it's not the same as the one that was clicked
				if (current === subMenu && !isOpen) {
					return null;
				}

				return subMenu;
			});
		},
		[],
	);

	return (
		<DropdownMenu
			trigger={formatMessage(messages.refineButton)}
			testId="refine-dropdown"
			shouldRenderToParent
			isOpen={isOpened}
			onOpenChange={handleOpenChange}
		>
			<RefineDropdownItems
				items={items}
				onItemClick={handleItemClick}
				currentOpenedSubMenu={currentOpenedSubMenu}
				onSubMenuOpenChanged={handleSubMenuOpenChanged}
			/>
		</DropdownMenu>
	);
}
